import React, { FC } from "react";
import cx from "classnames";
import _camelCase from "lodash/camelCase";
import {
  DatesFormat,
  ISelfTapeRequestStatus,
  MediaRequestType,
} from "shared-types";
import { dataTestIds } from "data-testids";
import { useTranslation } from "i18n";
import { IconTextLink } from "component-library";
import { EditIcon } from "assets";
import { getTranslationDateAndTime } from "shared/helpers/translationHelpers";
import StatusInfoBlock from "../StatusInfoBlock";

interface Props {
  selfTapeRequestStatus: ISelfTapeRequestStatus;
  mediaRequestType: MediaRequestType;
  openResponseModal: () => void;
  performerReplyTimestamp?: string;
  performerReplyBy?: string;
  performerReplyComments?: string;
}

const SelfTapeAcceptRejectBlock: FC<React.PropsWithChildren<Props>> = ({
  selfTapeRequestStatus,
  mediaRequestType,
  openResponseModal,
  performerReplyTimestamp,
  performerReplyBy,
  performerReplyComments,
}) => {
  const { t } = useTranslation();

  const isAccepted = selfTapeRequestStatus === ISelfTapeRequestStatus.Accepted;
  const isRejected = selfTapeRequestStatus === ISelfTapeRequestStatus.Rejected;
  const mediaRequestTransKey = _camelCase(MediaRequestType[mediaRequestType]);

  if (isAccepted || isRejected) {
    const isAbleToEditResponse = isAccepted || isRejected;

    return (
      <StatusInfoBlock
        title={t(
          `common:selfTapes.request.banner.title.${
            isAccepted ? "accepted" : "declined"
          }`,
          {
            date: t(
              "common:datesFormat.date.time.at",
              getTranslationDateAndTime({
                date: performerReplyTimestamp,
                dateFormat: DatesFormat.dateFormatPrimary,
                timeFormat: DatesFormat.timeFormatPrimary,
              })
            ),
            user: performerReplyBy,
          }
        )}
        description={
          <>
            {t("common:selfTapes.request.banner.message", {
              message: performerReplyComments,
            })}
            {isAccepted && (
              <div className="mt-4 text-s">
                {t("common:selfTapes.request.cta.submitRequest", {
                  mediaRequestType: t(
                    `common:selfTapes.request.cta.submitRequest.${mediaRequestTransKey}`
                  ),
                })}
              </div>
            )}
          </>
        }
        className={cx({
          "border border-orange-burnt bg-orange-pale": isAccepted,
          "border border-red-cardinal bg-red-pale": isRejected,
        })}
        data-testid={
          dataTestIds.nuJobsApp["SelfTapeAcceptRejectBlock.Response.Banner"]
        }
      >
        {isAbleToEditResponse && (
          <div>
            <IconTextLink
              text={t("common:button.label.editResponse")}
              svgIconStart={<EditIcon />}
              variant="secondary"
              className="bg-white font-semibold"
              onClick={openResponseModal}
            />
          </div>
        )}
      </StatusInfoBlock>
    );
  }

  return null;
};

export default SelfTapeAcceptRejectBlock;
